import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RevenueChart } from '@troia-v3/types';

import { ChartLegendChip, DashCard, ViewToggle, formatBRL, formatNumber, formatPct } from '@/shared/components/ui';

import type { CommercialChartMode } from '../hooks/useCommercialDashboardPreferences';

interface RevenueChartCardProps {
  data: RevenueChart;
  mode: CommercialChartMode;
  onModeChange: (mode: CommercialChartMode) => void;
}

const formatBRLShort = (v: number): string => {
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
  return `R$ ${v}`;
};

const PT_MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const PT_MONTHS_FULL = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/**
 * Cor por TOKEN, uma por grandeza — mesma convenção do gráfico de visitas do
 * Analytics, para "verde = vendas" valer nas duas telas.
 */
const VISITS_COLOR = 'var(--theme-primary)';
const LEADS_COLOR = 'var(--theme-info-color)';
const DEALS_COLOR = 'var(--theme-success-color)';

/** Tooltip de gráfico = tema claro fixo (padrão do projeto). */
const TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  backgroundColor: '#ffffff',
  color: '#1e293b',
  fontSize: 12,
};

const SERIES_LABEL: Record<string, string> = {
  revenue: 'Receita',
  deals: 'Vendas',
  leads: 'Leads',
  visits: 'Visitas',
};

const MODES = [
  { id: 'revenue' as const, icon: 'dollar-sign' as const, label: 'Receita' },
  { id: 'count' as const, icon: 'bar-chart' as const, label: 'Quantidade' },
];

/**
 * Receita por dia/mês OU, no modo Quantidade, vendas, leads e visitas no
 * mesmo eixo de tempo — o que o cliente queria comparar.
 *
 * ⚠️ Dois eixos no modo Quantidade, pelo mesmo motivo do card de visitas do
 * Analytics: visitas são centenas por dia e vendas, unidades. No mesmo eixo a
 * linha de vendas vira um traço no chão. Visitas leem a ESQUERDA; leads e
 * vendas (mesma unidade — venda é lead ganho) a DIREITA.
 *
 * Visita `null` = sem medição, e a linha quebra ali em vez de despencar a zero.
 */
export const RevenueChartCard: React.FC<RevenueChartCardProps> = ({ data, mode, onModeChange }) => {
  // `granularity` pode faltar em resposta anterior ao campo — aí vale a
  // detecção antiga pelo formato (`YYYY-MM-01` em todos os pontos = mensal).
  const isMonthly =
    data.granularity !== undefined
      ? data.granularity === 'month'
      : data.points.length > 0 && data.points.every((p) => p.date.endsWith('-01'));

  // Semana ou período que cruza a virada do mês: o dia sozinho seria ambíguo
  // ("01" depois de "30"), então o rótulo leva o mês junto.
  const spansMonths = new Set(data.points.map((p) => p.date.slice(0, 7))).size > 1;
  const dayLabel = (date: string): string =>
    spansMonths ? `${date.slice(8, 10)}/${date.slice(5, 7)}` : date.slice(8, 10);

  const chartData = data.points.map((p) => ({
    label: isMonthly ? PT_MONTHS_SHORT[Number(p.date.slice(5, 7)) - 1] ?? p.date : dayLabel(p.date),
    fullLabel: isMonthly
      ? PT_MONTHS_FULL[Number(p.date.slice(5, 7)) - 1] ?? p.date
      : `Dia ${dayLabel(p.date)}`,
    revenue: p.revenue,
    deals: p.deals ?? 0,
    leads: p.leads ?? 0,
    visits: p.visits ?? null,
  }));

  const totalDeals = chartData.reduce((s, p) => s + p.deals, 0);
  const totalLeads = chartData.reduce((s, p) => s + p.leads, 0);
  const totalVisits = chartData.reduce((s, p) => s + (p.visits ?? 0), 0);
  // Sem nenhuma medição no período, a série de visitas some do gráfico e da
  // legenda — empresa sem contador de fluxo não precisa ver "Visitas 0".
  const hasVisits = chartData.some((p) => p.visits !== null);

  const unit = isMonthly ? 'mês' : 'dia';
  const counter = isMonthly
    ? `${data.points.length} ${data.points.length === 1 ? 'mês' : 'meses'}`
    : `${data.points.length} dias`;
  const headerTitle = mode === 'revenue' ? `Receita por ${unit}` : `Vendas por ${unit}`;
  const subtitle =
    mode === 'revenue'
      ? `Receita ganha no período · ${counter}`
      : `${hasVisits ? 'Vendas, leads e visitas' : 'Vendas e leads'} · ${counter}`;

  const renderLastDot = (props: { cx?: number; cy?: number; index?: number }): React.ReactElement => {
    const isLast = props.index === chartData.length - 1;
    if (!isLast || props.cx == null || props.cy == null) {
      return <g key={`rev-dot-${props.index ?? 'x'}`} />;
    }
    return (
      <g key={`rev-dot-${props.index}`}>
        <circle cx={props.cx} cy={props.cy} r={7} fill="var(--theme-primary, #8b5cf6)" opacity={0.18} />
        <circle cx={props.cx} cy={props.cy} r={4} fill="var(--theme-primary, #8b5cf6)" />
      </g>
    );
  };

  const labelFormatter = (_l: unknown, payload: ReadonlyArray<{ payload?: { fullLabel?: string } }>): string =>
    payload[0]?.payload?.fullLabel ?? '';

  const axisTick = { fontSize: 10, fill: 'var(--theme-muted-text)' };

  return (
    <DashCard
      icon="trending-up"
      title={headerTitle}
      subtitle={subtitle}
      headerRight={
        <ViewToggle
          modes={MODES}
          value={mode}
          onChange={onModeChange}
          collapsibleLabels={false}
          data-testid="revenue-chart-mode"
        />
      }
      testId="revenue-chart-card"
      className="h-full"
      bodyClassName="flex flex-col"
    >
      {mode === 'count' && (
        <div
          className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 mb-2"
          data-testid="revenue-chart-legend"
        >
          {hasVisits && (
            <ChartLegendChip
              color={VISITS_COLOR}
              label="Visitas"
              total={totalVisits}
              testId="revenue-chart-legend-visits"
            />
          )}
          <ChartLegendChip
            color={LEADS_COLOR}
            label="Leads"
            total={totalLeads}
            testId="revenue-chart-legend-leads"
          />
          <ChartLegendChip
            color={DEALS_COLOR}
            label="Vendas"
            total={totalDeals}
            {...(hasVisits && totalVisits > 0
              ? { extra: `${formatPct((totalDeals / totalVisits) * 100)} das visitas` }
              : {})}
            testId="revenue-chart-legend-deals"
          />
        </div>
      )}
      <div className="flex-1 min-h-0" style={{ width: '100%', minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          {mode === 'revenue' ? (
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="grad-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--theme-primary, #8b5cf6)" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="var(--theme-primary, #8b5cf6)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={formatBRLShort} tick={axisTick} axisLine={false} tickLine={false} width={50} />
              <Tooltip
                formatter={(v) => [formatBRL(typeof v === 'number' ? v : Number(v ?? 0)), 'Receita']}
                labelFormatter={labelFormatter}
                contentStyle={TOOLTIP_STYLE}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="var(--theme-primary, #8b5cf6)"
                strokeWidth={2.5}
                fill="url(#grad-rev)"
                dot={renderLastDot}
              />
            </AreaChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--theme-card-border)" />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
              {hasVisits && (
                <YAxis
                  yAxisId="visits"
                  tickFormatter={(v: number) => formatNumber(v)}
                  tick={axisTick}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
              )}
              {/* Piso 1 evita um eixo 0..0 ilegível no período sem nenhum
                  lead, e `allowDecimals={false}` impede "0,5 venda". */}
              <YAxis
                yAxisId="count"
                orientation={hasVisits ? 'right' : 'left'}
                domain={[0, (max: number) => Math.max(1, max)]}
                allowDecimals={false}
                tick={axisTick}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip
                formatter={(v, name) => [
                  v === null || v === undefined ? 'sem medição' : formatNumber(Number(v)),
                  SERIES_LABEL[String(name)] ?? String(name),
                ]}
                labelFormatter={labelFormatter}
                contentStyle={TOOLTIP_STYLE}
              />
              {hasVisits && (
                <Line
                  yAxisId="visits"
                  type="monotone"
                  dataKey="visits"
                  name="visits"
                  stroke={VISITS_COLOR}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              )}
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="leads"
                name="leads"
                stroke={LEADS_COLOR}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="deals"
                name="deals"
                stroke={DEALS_COLOR}
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </DashCard>
  );
};

RevenueChartCard.displayName = 'RevenueChartCard';
